import { UserAccount } from "@/app/identity-access-management/authentication/model/user-account.ts";

export type AuthState =
    | { status: "loading"; userAccount: null }
    | { status: "authenticated"; userAccount: UserAccount }
    | { status: "anonymous"; userAccount: null };

const CURRENT_USER_STORAGE_KEY = "currentUser";
export const URL_LOGIN = `/api/v1/user/login`;
export const URL_LOGOUT = `/api/v1/user/logout`;
const URL_IS_LOGGED_IN = `/api/v1/user/is-logged-in`;

export function persistUserAccount(userAccount: UserAccount) {
    sessionStorage.setItem(
        CURRENT_USER_STORAGE_KEY,
        JSON.stringify(userAccount),
    );
}

export function clearStoredUserAccount() {
    sessionStorage.removeItem(CURRENT_USER_STORAGE_KEY);
}

function getStoredUserAccount(): UserAccount | null {
    const currentUserAsString = sessionStorage.getItem(
        CURRENT_USER_STORAGE_KEY,
    );

    if (!currentUserAsString) {
        return null;
    }

    try {
        return JSON.parse(currentUserAsString) as UserAccount;
    } catch {
        clearStoredUserAccount();
        return null;
    }
}

function getStoredAuthState(): AuthState | null {
    const userAccount = getStoredUserAccount();

    if (!userAccount) {
        return null;
    }

    return { status: "authenticated", userAccount };
}

export async function readAuthStateFromBackend(): Promise<AuthState> {
    const response = await fetch(URL_IS_LOGGED_IN, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
        clearStoredUserAccount();
        return { status: "anonymous", userAccount: null };
    }

    const userLoggedIn: UserAccount = await response.json();
    persistUserAccount(userLoggedIn);

    return { status: "authenticated", userAccount: userLoggedIn };
}

export async function getInitialAuthState(): Promise<AuthState> {
    try {
        return await readAuthStateFromBackend();
    } catch {
        return getStoredAuthState() ?? {
            status: "loading",
            userAccount: null,
        };
    }
}
